import { useEffect, useState, useRef, forwardRef } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import styled from 'styled-components';
import Divider from '@mui/material/Divider';
import DownloadIcon from '@mui/icons-material/Download';
import './index.css';
import { useConfig } from '../../../context/configContext';
import { getPlumePreviewUrl } from '../../../utils/previewUrl';

const HorizontalLayout = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: 5px;
  margin-bottom: 5px;
  overflow: auto;
`;

const HighlightableCard = styled(Card)`
  transition: border 0.3s ease;
  box-shadow: none;
  cursor: pointer;
  &:hover {
    border: 1px solid blue;
  }
  border: ${(props) =>
    //eslint-disable-next-line prettier/prettier
    props.$isHovered ? '1px solid #4866ff' : '1px solid transparent'};
`;

const MAX_IMAGE_RETRIES = 2;
const RETRY_DELAY_MS = 600;

const CaptionValue = ({ caption, value, className }) => {
  return (
    <div className={className}>
      {/* eslint-disable-next-line prettier/prettier */}
      <Typography
        variant='caption'
        component='div'
        sx={{
          color: '#808080',
          fontWeight: 500,
          unicodeBidi: 'isolate',
          fontFamily: "Public sans",
          fontSize: 11,
          lineHeight: 1.2,
        }}
      >
        {caption}
      </Typography>
      {/* eslint-disable-next-line prettier/prettier */}
      <Typography
        variant='body2'
        component='div'
        sx={{
          color: 'var(--main-grey)',
          fontSize: 13,
          wordWrap: 'break-word',
          overflowWrap: 'break-word',
        }}
      >
        {value}
      </Typography>
    </div>
  );
};
/**
 * VisualizationItemCard
 *
 * Displays detailed plume information in a card layout with preview image, metadata,
 * and interaction handlers for hover and selection.
 *
 * @param {Object} props
 * @param {Object} props.vizItem - Plume STAC item object.
 * @param {string} props.collectionId - ID of the STAC collection.
 * @param {Function} props.onSelectVizLayer - Callback triggered when card is clicked.
 * @param {Function} props.onHoverOnVizLayer - Callback triggered on mouse enter/leave.
 * @param {string} props.hoveredVizItemId - ID of the currently hovered item (used for highlight sync).
 * @param {Ref} ref - React ref to support scroll-to or measurement.
 *
 * @returns {JSX.Element}
 */
export const VisualizationItemCard = forwardRef(
  (
    {
      vizItem,
      collectionId,
      onSelectVizLayer,
      onHoverOnVizLayer,
      hoveredVizItemId,
    },
    ref
  ) => {
    const { config } = useConfig();
    const rasterApiUrl = config.rasterApiUrl;
    const vizItemSourceId = vizItem?.id;
    const orbit = vizItem?.plumeProperties?.orbit;
    const imageUrl = getPlumePreviewUrl(
      rasterApiUrl,
      collectionId,
      vizItemSourceId,
      vizItem?.polygonGeometry
    );

    // The raster API intermittently drops an image request, which would
    // otherwise leave a permanently broken thumbnail on the card.
    const [imageAttempt, setImageAttempt] = useState(0);
    const retryTimer = useRef(null);
    const imageSrc =
      imageAttempt === 0 ? imageUrl : `${imageUrl}&retry=${imageAttempt}`;

    useEffect(() => {
      setImageAttempt(0);
    }, [imageUrl]);

    useEffect(() => () => clearTimeout(retryTimer.current), []);

    const handleImageError = () => {
      if (imageAttempt >= MAX_IMAGE_RETRIES) return;
      clearTimeout(retryTimer.current);
      retryTimer.current = setTimeout(
        () => setImageAttempt((attempt) => attempt + 1),
        RETRY_DELAY_MS * (imageAttempt + 1)
      );
    };

    const tiffUrl = vizItem?.plumeProperties?.assetLink;
    const location = vizItem?.plumeProperties?.location;
    const maxPlumeConcentration = vizItem?.plumeProperties?.maxConcentration;
    const concentrationUncertanity =
      vizItem?.plumeProperties?.concentrationUncertanity;
    const utcTimeObserved = vizItem?.plumeProperties?.utcTimeObserved;
    const latitudeOfMaxConcentration =
      vizItem?.plumeProperties?.latitudeOfMaxConcentration;
    const longitudeOfMaxConcentration =
      vizItem?.plumeProperties?.longitudeOfMaxConcentration;

    const [isHovered, setIsHovered] = useState(false);

    const handleCardClick = () => {
      onSelectVizLayer && onSelectVizLayer(vizItemSourceId);
    };

    const handleMouseEnter = () => {
      onHoverOnVizLayer(vizItemSourceId);
    };

    const handleMouseLeave = () => {
      onHoverOnVizLayer('');
    };
    useEffect(() => {
      if (hoveredVizItemId === vizItemSourceId) setIsHovered(true);
      if (hoveredVizItemId !== vizItemSourceId) setIsHovered(false);
    }, [hoveredVizItemId, vizItemSourceId]);
    return (
      <div
        className="card-container"
        style={{
          width: "100%",
          maxWidth: "94%",
          marginLeft: "auto",
          marginRight: "auto"
        }}
        ref={ref}
      >
        <HighlightableCard
          className="responsive-card"
          onClick={handleCardClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          $isHovered={isHovered}
        >
          <div
            className="card-image-container"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              alignSelf: 'center',
              // Each cropped preview has its own aspect ratio, so the box is a
              // fixed size and the image is contained inside it; sizing to the
              // image would make this column a different width on every card.
              boxSizing: 'content-box',
              width: '120px',
              height: '100px',
              padding: '0.8em',
            }}
          >
            <CardMedia
              component='img'
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                imageRendering: 'pixelated',
              }}
              image={imageSrc}
              onError={handleImageError}
              alt='plume image'
            />
          </div>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              fontFamily: "Sans",
              flex: 1,
              minWidth: 0
            }}
          >
            <CardContent sx={{ flex: '1 0 auto', width: "100%", overflow: "hidden", maxWidth: "100%" }}>
              <HorizontalLayout>
                <CaptionValue
                  className='card-plume'
                  caption='ID'
                  value={vizItemSourceId}
                />
                <CaptionValue
                  className='card-plume'
                  caption='Orbit'
                  value={orbit}
                />
              </HorizontalLayout>

              <HorizontalLayout>
                {/* eslint-disable-next-line prettier/prettier */}
                <a
                  href={tiffUrl}
                  target='_blank'
                  rel='noreferrer'
                  className='card-download-link'
                >
                  <Typography variant='caption' component='div'>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      Download the Tiff File <DownloadIcon fontSize='small' />
                    </div>
                  </Typography>
                </a>
              </HorizontalLayout>
              <Divider></Divider>
              <HorizontalLayout>
                <CaptionValue
                  className='card-plume'
                  caption='Location'
                  value={location}
                />
                <CaptionValue
                  className='card-plume'
                  caption='UTC Time Observed'
                  value={utcTimeObserved}
                />
              </HorizontalLayout>
              <HorizontalLayout>
                <CaptionValue
                  className='card-plume'
                  caption='Max Plume Concentration'
                  value={maxPlumeConcentration + ' ppm m'}
                />
                <CaptionValue
                  className='card-plume'
                  caption='Concentration Uncertainity'
                  value={concentrationUncertanity + ' ppm m'}
                />
              </HorizontalLayout>
              <HorizontalLayout>
                <CaptionValue
                  className='card-plume'
                  caption='Latitude (Max Conc)'
                  value={Number(latitudeOfMaxConcentration).toFixed(3)}
                />
                <CaptionValue
                  className='card-plume'
                  caption='Longitude (Max Conc)'
                  value={Number(longitudeOfMaxConcentration).toFixed(3)}
                />

              </HorizontalLayout>
            </CardContent>
          </Box>
        </HighlightableCard>
      </div>
    );
  }
);
